import { IconButton, Icons } from '@commenda/kakashi';
import type { ReactNode } from 'react';

interface QuestionCardShellProps {
  /** Header eyebrow: "Question" for the select and upload cards, "Plan" for plan approval. */
  label: string;
  /** What the group announces to assistive tech — the question text, or the plan title. */
  ariaLabel: string;
  /** Pager (select cards) or collapse chevron (plan card), rendered left of the close X. */
  headerControls?: ReactNode;
  /** The close X: collapse to the recap without sending anything. */
  onClose: () => void;
  /** Status text on the footer's left ("Select one to continue", "2 of 3 selected"). */
  status?: ReactNode;
  /** Footer actions on the right (Skip, Continue, Approve plan). */
  actions?: ReactNode;
  children: ReactNode;
}

/** Shared chrome for every question card, per Figma node 1220:606: a bg-light tray carrying the
 * header and footer, with the question itself on a white card inset by 6px inside it. The polite
 * status line tells a screen-reader user that an answerable card just appeared mid-stream, which
 * the visual skeleton-to-live transition never announces. */
export function QuestionCardShell({
  label,
  ariaLabel,
  headerControls,
  onClose,
  status,
  actions,
  children,
}: QuestionCardShellProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex w-full flex-col overflow-hidden rounded-2xl border-[0.5px] border-border-tertiary bg-bg-light shadow-[0px_4px_24px_0px_rgba(0,0,0,0.04)]"
    >
      <span className="sr-only" role="status">
        {`${label}: ${ariaLabel}`}
      </span>
      <div className="flex w-full items-center gap-lg px-2xl py-md">
        <p className="min-w-0 flex-1 txt-small-medium text-text-tertiary">
          {label}
        </p>
        {headerControls}
        <IconButton
          size="sm"
          icon={<Icons.XClose className="size-4" />}
          aria-label="Dismiss"
          onClick={onClose}
        />
      </div>
      <div className="flex w-full flex-col gap-lg px-sm pb-sm">
        <div className="flex w-full flex-col gap-xl rounded-2xl border-[0.5px] border-border-tertiary bg-bg-primary p-xl shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
          {children}
        </div>
      </div>
      <div className="flex w-full items-center justify-between gap-lg px-xl py-md">
        <span className="txt-extra-small-regular text-text-tertiary">
          {status}
        </span>
        <div className="flex shrink-0 items-center gap-md">{actions}</div>
      </div>
    </div>
  );
}
